import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { useCreateWorkspaceDialog, useSelectedStore } from '@web/app/shared';
import { AxiosError } from 'axios';

import { useOwnerStore } from '@codes/owner';
import { useFetchProductsByProjectId } from '@codes/product';
import { useProjects } from '@codes/project';
import { isProdEnvironment, useLogout } from '@codes/web-core';
import { useMyWorkspaces, useWorkspace } from '@codes/workspace';

export const useWorkspaceService = () => {
    const isProd = isProdEnvironment();
    const navigate = useNavigate();

    const { refresh } = useCreateWorkspaceDialog({});
    const { mutateAsync: logout } = useLogout();
    const ownerInfo = useOwnerStore(state => state.owner);

    const { workspace, project, setProject, setWorkspace, siteId, setSiteId } = useSelectedStore();

    const { data: workspaceFromServer, error: fetchWorkspaceFromServerError } = useWorkspace('0');

    const myWorkspacesQueryResult = useMyWorkspaces();

    const myProjectsQueryResult = useProjects(workspaceFromServer?.siteId as string, {
        limit: 50,
        page: 0,
    });

    const myProductsQueryResult = useFetchProductsByProjectId(project?.id || '', {});

    const myWorkspaces =
        !!myWorkspacesQueryResult.data && !!myWorkspacesQueryResult.data.data ? myWorkspacesQueryResult.data.data : [];

    const myProjects = useMemo(() => {
        return !!myProjectsQueryResult.data && !!myProjectsQueryResult.data.data ? myProjectsQueryResult.data.data : [];
    }, [myProjectsQueryResult.data]);

    const myProducts = useMemo(() => {
        return !!myProductsQueryResult.data && !!myProductsQueryResult.data.data ? myProductsQueryResult.data.data : [];
    }, [myProductsQueryResult.data]);

    const areMyWorkspacesEmpty =
        !!myWorkspacesQueryResult.data &&
        !!myWorkspacesQueryResult.data.data &&
        myWorkspacesQueryResult.data.total === 0;

    const areMyProjectsEmpty =
        !!myProjectsQueryResult.data && !!myProjectsQueryResult.data.data && myProjectsQueryResult.data.total === 0;

    const areMyProductsEmpty =
        !!myProductsQueryResult.data && !!myProductsQueryResult.data.data && myProductsQueryResult.data.total === 0;

    const isApprovedWorkspace = useMemo(() => {
        const isDefault = workspaceFromServer?.name === 'default';

        if (isProd && isDefault) {
            return true;
        }

        return !!workspaceFromServer?.approvedAt;
    }, [workspaceFromServer, isProd]);

    useEffect(() => {
        if (!workspaceFromServer) {
            return;
        }

        // Always sync if it's a different workspace
        if (!workspace || workspace.id !== workspaceFromServer.id || workspace.siteId !== workspaceFromServer.siteId) {
            setWorkspace(workspaceFromServer);
            return;
        }

        // Check specific fields that should trigger sync (avoid expensive JSON.stringify)
        const hasRelevantChanges =
            workspace.geminiApiKey !== workspaceFromServer.geminiApiKey ||
            workspace.name !== workspaceFromServer.name ||
            workspace.regionId !== workspaceFromServer.regionId ||
            workspace.approvedAt !== workspaceFromServer.approvedAt;

        if (hasRelevantChanges) {
            setWorkspace(workspaceFromServer);
        }
    }, [workspaceFromServer, workspace, setWorkspace]);

    useEffect(() => {
        if (!fetchWorkspaceFromServerError) {
            return;
        }

        const axiosError = fetchWorkspaceFromServerError as AxiosError<string>;
        toast({ description: axiosError.response?.data || 'unknown error' });

        const defaultWorkspace = myWorkspaces.find(workspace => workspace.id === ownerInfo?.defWorkspaceId);

        if (!defaultWorkspace) {
            logout();
            return;
        }

        refresh(defaultWorkspace?.siteId as string);
    }, [fetchWorkspaceFromServerError, myWorkspaces, ownerInfo]);

    return {
        myWorkspacesQueryResult,
        myWorkspaces,
        areMyWorkspacesEmpty,
        myProjectsQueryResult,
        myProjects,
        areMyProjectsEmpty,
        myProductsQueryResult,
        myProducts,
        areMyProductsEmpty,
        isApprovedWorkspace,
        workspaceFromServer,
    };
};
